import { type EnvironmentId } from "@cmmd-forge/contracts";
import { type Thread } from "../../types";
import { useCheckpointRevert } from "./useCheckpointRevert";
import { useChatInterrupt } from "./useChatInterrupt";
import { useChatTurnSubmission } from "./useChatTurnSubmission";
import { useGitHubApprovalControls } from "./useGitHubApprovalControls";
import { usePendingAutoSubmit } from "./usePendingAutoSubmit";
import { usePlanFollowUpControls } from "./usePlanFollowUpControls";
import { useProviderModelSelectionControls } from "./useProviderModelSelectionControls";
import { useQueuedTurnDispatch } from "./useQueuedTurnDispatch";
import { useTimelineRevertControls } from "./useTimelineRevertControls";

/** Connects turn submission, queued dispatch, interruption, and thread action controls. */
export function useChatTurnActionController<ComposerTarget>(
  input: ChatTurnActionControllerInput<ComposerTarget>,
) {
  const checkpoint = useCheckpointRevert(input.checkpoint);
  const interrupt = useChatInterrupt({
    environmentId: input.environmentId,
    activeThread: input.activeThread,
  });
  const gitHub = useGitHubApprovalControls(input.gitHub);
  const planFollowUp = usePlanFollowUpControls(input.planFollowUp);
  const send = useChatTurnSubmission({
    ...input.send,
    onSubmitPlanFollowUp: planFollowUp.onSubmitPlanFollowUp,
  });
  const queuedTurn = useQueuedTurnDispatch({
    queueContext: input.send.queueContext,
    blockedByPendingRequest: input.send.hasActivePendingProgress,
    isSendInFlight: send.isSendInFlight,
    onSend: send.onSend,
  });
  usePendingAutoSubmit({ ...input.pendingAutoSubmit, onSend: send.onSend });
  const providerModel = useProviderModelSelectionControls(input.providerModel);
  const onRevertUserMessage = useTimelineRevertControls({
    revertTurnCountByUserMessageId: input.revertTurnCountByUserMessageId,
    onRevertToTurnCount: checkpoint.onRevertToTurnCount,
  });

  return {
    ...gitHub,
    ...planFollowUp,
    ...send,
    ...queuedTurn,
    ...providerModel,
    onInterrupt: interrupt,
    onRevertUserMessage,
  };
}

export interface ChatTurnActionControllerInput<ComposerTarget> {
  readonly environmentId: EnvironmentId;
  readonly activeThread: Thread | undefined;
  readonly revertTurnCountByUserMessageId: ReadonlyMap<Thread["messages"][number]["id"], number>;
  readonly checkpoint: Parameters<typeof useCheckpointRevert>[0];
  readonly gitHub: Parameters<typeof useGitHubApprovalControls<ComposerTarget>>[0];
  readonly planFollowUp: Parameters<typeof usePlanFollowUpControls>[0];
  readonly send: Omit<
    Parameters<typeof useChatTurnSubmission<ComposerTarget>>[0],
    "onSubmitPlanFollowUp"
  >;
  readonly pendingAutoSubmit: Omit<Parameters<typeof usePendingAutoSubmit>[0], "onSend">;
  readonly providerModel: Parameters<typeof useProviderModelSelectionControls>[0];
}
